/**
=========================================================
* Comparison Report Page
=========================================================
* Admin report comparing series_participants and req_series_participants
*/

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { publicRegAdminAPI } from 'services/api';

const ComparisonReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [seriesId, setSeriesId] = useState(null);
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0: both, 1: only_req, 2: only_series
  const [reportData, setReportData] = useState(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const seriesKeyFromUrl = urlParams.get('s');

  useEffect(() => {
    if (seriesKeyFromUrl) {
      setSeriesId(seriesKeyFromUrl);
    }
  }, [seriesKeyFromUrl]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (seriesId) {
      loadReport();
    }
  }, [seriesId, debouncedSearchTerm]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await publicRegAdminAPI.getComparisonReport({
        series_id: seriesId,
        search_term: debouncedSearchTerm || '',
      });

      if (response.data.status === 1) {
        setReportData(response.data);
        if (response.data.series_info) {
          setSeriesInfo(response.data.series_info);
        }
      } else {
        toast.error(response.data.message || 'Failed to load comparison report');
      }
    } catch (error) {
      console.error('Error loading comparison report:', error);
      toast.error(error.response?.data?.message || 'Failed to load comparison report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!reportData) return;

    const csvRows = [];
    const headers = [];

    // Export based on active tab
    let dataToExport = [];
    let categoryName = '';

    switch (activeTab) {
      case 0: // Both
        dataToExport = reportData.both || [];
        categoryName = 'Both';
        headers.push('Player Name', 'Mobile No', 'User ID', 'Series Participant ID', 'SL No', 'In Waiting List', 'Req Participant ID', 'Actual Series Participant ID', 'Req Status', 'Category', 'Request Added On', 'Approved On');
        csvRows.push(headers.join(','));
        dataToExport.forEach(item => {
          csvRows.push([
            `"${item.player_name || ''}"`,
            item.mobile_no || '',
            item.user_id || '',
            item.series_participant_id || '',
            item.sl_no || '',
            item.in_waiting_list || 0,
            item.req_series_participant_id || '',
            item.actual_series_participant_id || '',
            item.req_status || '',
            item.category || '',
            item.req_added_on || '',
            item.approved_on || '',
          ].join(','));
        });
        break;
      case 1: // Only Req
        dataToExport = reportData.only_req || [];
        categoryName = 'Only_Requests';
        headers.push('Player Name', 'Mobile No', 'Req Participant ID', 'Internal SL No', 'Req Status', 'Request Status', 'Rejected On', 'Rejection Reason', 'Request Added On');
        csvRows.push(headers.join(','));
        dataToExport.forEach(item => {
          csvRows.push([
            `"${item.player_name || ''}"`,
            item.mobile_no || '',
            item.req_series_participant_id || '',
            item.internal_req_sl_no || '',
            item.req_status || '',
            item.request_status || '',
            item.rejected_on || '',
            `"${item.rejection_reason || ''}"`,
            item.req_added_on || '',
          ].join(','));
        });
        break;
      case 2: // Only Series
        dataToExport = reportData.only_series || [];
        categoryName = 'Only_Series';
        headers.push('Player Name', 'Mobile No', 'User ID', 'Series Participant ID', 'SL No', 'In Waiting List', 'Series Added On');
        csvRows.push(headers.join(','));
        dataToExport.forEach(item => {
          csvRows.push([
            `"${item.player_name || ''}"`,
            item.mobile_no || '',
            item.user_id || '',
            item.series_participant_id || '',
            item.sl_no || '',
            item.in_waiting_list || 0,
            item.series_added_on || '',
          ].join(','));
        });
        break;
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `comparison_report_${categoryName}_${seriesInfo?.series_name || seriesId}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Report exported successfully');
  };

  if (loading && !reportData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!reportData) {
    return (
      <div className="container-fluid py-4 bg-light min-vh-100">
        <Box sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Comparison Report
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            No data available. Please check the series ID.
          </Alert>
        </Box>
      </div>
    );
  }

  const summary = reportData.summary || {};
  const bothData = reportData.both || [];
  const onlyReqData = reportData.only_req || [];
  const onlySeriesData = reportData.only_series || [];

  return (
    <div className="container-fluid py-4 bg-light min-vh-100">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Comparison Report
            </Typography>
            {seriesInfo?.series_name && (
              <Typography variant="h6" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {seriesInfo.series_name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#E8F5E9', border: '2px solid #4CAF50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ color: '#2E7D32', mr: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                    {summary.total_in_both || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  In Both (Approved)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Participants with approved requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#FFF3E0', border: '2px solid #FF9800' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PendingIcon sx={{ color: '#F57C00', mr: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#F57C00' }}>
                    {summary.total_only_req || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Only Requests
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Pending: {summary.total_pending || 0} | Rejected: {summary.total_rejected || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#E3F2FD', border: '2px solid #2196F3' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InfoIcon sx={{ color: '#1976D2', mr: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976D2' }}>
                    {summary.total_only_series || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Only Series (Direct)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Directly added without request
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#F5F5F5', border: '2px solid #757575' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#424242', mb: 1 }}>
                  {summary.total_approved || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Total Approved
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  All approved requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Box */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by mobile number or player name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{ mr: -1 }}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            {searchTerm && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Searching... Results will update automatically
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2 }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  <span>Both ({bothData.length})</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PendingIcon sx={{ fontSize: 18 }} />
                  <span>Only Requests ({onlyReqData.length})</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon sx={{ fontSize: 18 }} />
                  <span>Only Series ({onlySeriesData.length})</span>
                </Box>
              }
            />
          </Tabs>

          {/* Export Button */}
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              size="small"
            >
              Export CSV
            </Button>
          </Box>

          <Divider />

          {/* Tab Content */}
          <CardContent>
            {/* Both Tab */}
            {activeTab === 0 && (
              <Box>
                {bothData.length === 0 ? (
                  <Alert severity="info">
                    No participants found in both tables {searchTerm && 'matching your search'}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                    <Table>
                      <TableBody>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Player Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Mobile No</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>SL No</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Waiting List</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Request ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Request Added</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Approved On</TableCell>
                        </TableRow>
                        {bothData.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                              '&:hover': { bgcolor: '#f0f0f0' }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{item.player_name}</TableCell>
                            <TableCell>{item.mobile_no}</TableCell>
                            <TableCell align="center">{item.sl_no || '-'}</TableCell>
                            <TableCell align="center">
                              {item.in_waiting_list == 1 ? (
                                <Chip label="Yes" size="small" color="warning" />
                              ) : (
                                <Chip label="No" size="small" color="success" />
                              )}
                            </TableCell>
                            <TableCell>#{item.req_series_participant_id}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.category === 'approved' ? 'Approved' : 'Linked by Mobile'}
                                size="small"
                                color={item.category === 'approved' ? 'success' : 'info'}
                              />
                            </TableCell>
                            <TableCell>
                              {item.req_added_on ? new Date(item.req_added_on).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell>
                              {item.approved_on ? new Date(item.approved_on).toLocaleString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Only Req Tab */}
            {activeTab === 1 && (
              <Box>
                {onlyReqData.length === 0 ? (
                  <Alert severity="info">
                    No participants found only in requests {searchTerm && 'matching your search'}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                    <Table>
                      <TableBody>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Player Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Mobile No</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Internal SL No</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Request ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Request Added</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Rejected On</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Rejection Reason</TableCell>
                        </TableRow>
                        {onlyReqData.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                              '&:hover': { bgcolor: '#f0f0f0' }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{item.player_name}</TableCell>
                            <TableCell>{item.mobile_no}</TableCell>
                            <TableCell align="center">{item.internal_req_sl_no || '-'}</TableCell>
                            <TableCell>#{item.req_series_participant_id}</TableCell>
                            <TableCell>
                              {item.request_status === 'pending' ? (
                                <Chip
                                  icon={<PendingIcon />}
                                  label="Pending"
                                  size="small"
                                  color="warning"
                                />
                              ) : (
                                <Chip
                                  icon={<CancelIcon />}
                                  label="Rejected"
                                  size="small"
                                  color="error"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {item.req_added_on ? new Date(item.req_added_on).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell>
                              {item.rejected_on ? new Date(item.rejected_on).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              {item.rejection_reason || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Only Series Tab */}
            {activeTab === 2 && (
              <Box>
                {onlySeriesData.length === 0 ? (
                  <Alert severity="info">
                    No participants found only in series {searchTerm && 'matching your search'}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                    <Table>
                      <TableBody>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Player Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Mobile No</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>User ID</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>SL No</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Waiting List</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Series Participant ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Series Added On</TableCell>
                        </TableRow>
                        {onlySeriesData.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                              '&:hover': { bgcolor: '#f0f0f0' }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{item.player_name}</TableCell>
                            <TableCell>{item.mobile_no}</TableCell>
                            <TableCell>{item.user_id || '-'}</TableCell>
                            <TableCell align="center">{item.sl_no || '-'}</TableCell>
                            <TableCell align="center">
                              {item.in_waiting_list == 1 ? (
                                <Chip label="Yes" size="small" color="warning" />
                              ) : (
                                <Chip label="No" size="small" color="success" />
                              )}
                            </TableCell>
                            <TableCell>#{item.series_participant_id}</TableCell>
                            <TableCell>
                              {item.series_added_on ? new Date(item.series_added_on).toLocaleString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default ComparisonReport;

